export const ROLE_CODES = [
    "BARANGAY_ENCODER",
    "BARANGAY_SUPERVISOR",
    "MUNICIPAL_CITY_USER",
    "MUNICIPAL_CITY_ADMINISTRATOR",
    "MAYOR_VIEWER",
    "SYSTEM_ADMINISTRATOR",
    "TECHNICAL_ADMINISTRATOR",
];
/**
 * Roles a Municipality/City Administrator may provision or assign.
 * Elevated administrator roles are intentionally excluded. The Mayor Viewer is
 * municipality-scoped and strictly read-only.
 */
export const MUNICIPAL_ADMIN_ASSIGNABLE_ROLES = [
    "BARANGAY_ENCODER",
    "BARANGAY_SUPERVISOR",
    "MUNICIPAL_CITY_USER",
    "MAYOR_VIEWER",
];
/**
 * Roles a System Administrator may provision or assign through the controlled
 * system-administration workflow. Operational Barangay/Municipality users stay
 * under the Municipality/City Administrator. Another System Administrator is
 * bootstrap-controlled and cannot be created through routine account management.
 */
export const SYSTEM_ADMIN_ASSIGNABLE_ROLES = [
    "MUNICIPAL_CITY_ADMINISTRATOR",
    "TECHNICAL_ADMINISTRATOR",
];
/**
 * Audit History is an administrative accountability surface. Operational
 * Barangay and Municipality/City users remain audited server-side but cannot
 * browse audit events themselves.
 */
export const AUDIT_VIEW_ROLES = [
    "MUNICIPAL_CITY_ADMINISTRATOR",
    "SYSTEM_ADMINISTRATOR",
    "TECHNICAL_ADMINISTRATOR",
];
export const PERMISSION_CODES = [
    "dashboard.view",
    "record.create",
    "record.view",
    "record.update",
    "record.delete",
    "record.review",
    "record.approve",
    "record.return",
    "report.view",
    "report.export",
    "certificate.view",
    "certificate.generate",
    "data.import",
    "data.export",
    "account.manage",
    "account.delete",
    "role.assign",
    "scope.assign",
    "audit.view",
    "settings.manage",
    "backup.monitor",
    "sync.monitor",
    "system.monitor",
    "ai.query",
];
const barangayEncoderPermissions = [
    "dashboard.view",
    "record.create",
    "record.view",
    "record.update",
    "report.view",
    "report.export",
    "data.export",
    "certificate.view",
    "certificate.generate",
];
const mayorViewerPermissions = [
    "dashboard.view",
    "record.view",
    "report.view",
    "ai.query",
];
export const ROLE_PERMISSION_PRESETS = {
    // One Barangay Encoder identity is used by both the offline-first Desktop and
    // the online Barangay portal. The legacy role remains readable only so an
    // upgraded deployment can migrate old assignments without breaking sessions.
    BARANGAY_ENCODER: barangayEncoderPermissions,
    BARANGAY_WEB_PORTAL: barangayEncoderPermissions,
    // The Barangay Supervisor is a superset of the Barangay Encoder: the
    // supervisor can use every Encoder module within the same Barangay scope,
    // while retaining review, approval/return, legacy-data import, and LIRA.
    BARANGAY_SUPERVISOR: [
        ...barangayEncoderPermissions,
        "record.review",
        "record.approve",
        "record.return",
        "data.import",
        "ai.query",
    ],
    MUNICIPAL_CITY_USER: [
        "dashboard.view",
        "record.create",
        "record.view",
        "record.update",
        "report.view",
        "report.export",
        "certificate.view",
        "certificate.generate",
        "data.export",
        "ai.query",
    ],
    MUNICIPAL_CITY_ADMINISTRATOR: [
        "dashboard.view",
        "record.view",
        "record.update",
        "record.delete",
        "report.view",
        "report.export",
        "certificate.view",
        "certificate.generate",
        "data.import",
        "data.export",
        "account.manage",
        "account.delete",
        "role.assign",
        "scope.assign",
        "audit.view",
        "settings.manage",
        "backup.monitor",
        "sync.monitor",
        "ai.query",
    ],
    MAYOR_VIEWER: mayorViewerPermissions,
    // System Administrator is the global administrative role and therefore has
    // every application permission. Destructive actions remain server-audited.
    SYSTEM_ADMINISTRATOR: PERMISSION_CODES,
    TECHNICAL_ADMINISTRATOR: [
        "audit.view",
        "settings.manage",
        "backup.monitor",
        "sync.monitor",
        "system.monitor",
    ],
};
export const WEB_PORTAL_ROLES = [
    "BARANGAY_ENCODER",
    "BARANGAY_WEB_PORTAL",
    "BARANGAY_SUPERVISOR",
    "MUNICIPAL_CITY_USER",
    "MUNICIPAL_CITY_ADMINISTRATOR",
    "MAYOR_VIEWER",
    "SYSTEM_ADMINISTRATOR",
    "TECHNICAL_ADMINISTRATOR",
];
export function roleCanUseWebPortal(role) {
    return WEB_PORTAL_ROLES.includes(role);
}
export function roleCanViewAudit(role) {
    return AUDIT_VIEW_ROLES.includes(role);
}
export function roleHasPermission(role, permission) {
    if (permission === "audit.view" && !roleCanViewAudit(role))
        return false;
    return ROLE_PERMISSION_PRESETS[role].includes(permission);
}
//# sourceMappingURL=access.js.map